import { useState, useRef, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  threshold?: number;
  maxPull?: number;
}

const PullToRefresh = ({ 
  children, 
  onRefresh, 
  threshold = 80,
  maxPull = 120 
}: PullToRefreshProps) => {
  const isMobile = useIsMobile();
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile || isRefreshing) return;
    
    // Только если скролл вверху страницы
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop > 5) return;
    
    startY.current = e.touches[0].clientY;
    isDragging.current = true;
  }, [isMobile, isRefreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || !isMobile || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    // Только если тянем вниз
    if (diff > 0) {
      // Уменьшаем скорость потягивания для более естественного ощущения
      const dampedDiff = Math.min(diff * 0.5, maxPull);
      setPullDistance(dampedDiff);
      setShowIndicator(true);
      
      // Предотвращаем стандартный скролл
      if (dampedDiff > 10) {
        e.preventDefault();
      }
    }
  }, [isMobile, isRefreshing, maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isDragging.current || !isMobile) return;
    
    isDragging.current = false;
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6);
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setShowIndicator(false);
      }
    } else {
      setPullDistance(0);
      setTimeout(() => setShowIndicator(false), 200);
    }
  }, [pullDistance, threshold, isRefreshing, onRefresh, isMobile]);

  // Вычисляем прогресс и готовность к обновлению
  const progress = Math.min(pullDistance / threshold, 1);
  const isReady = pullDistance >= threshold;

  if (!isMobile) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative min-h-screen"
    >
      {/* Индикатор обновления */}
      <AnimatePresence>
        {showIndicator && (
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ 
              opacity: 1, 
              y: pullDistance - 40,
            }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center justify-center"
            style={{ top: 60 }}
          >
            <motion.div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                backdrop-blur-md border shadow-lg
                ${isReady || isRefreshing 
                  ? 'bg-primary/20 border-primary/40 text-primary' 
                  : 'bg-background/80 border-border text-muted-foreground'
                }
              `}
              animate={{
                scale: isReady ? 1.1 : 1,
                rotate: isRefreshing ? 360 : progress * 180,
              }}
              transition={{
                rotate: isRefreshing 
                  ? { duration: 1, repeat: Infinity, ease: 'linear' }
                  : { duration: 0.1 },
                scale: { type: 'spring', stiffness: 400, damping: 15 }
              }}
            >
              <RefreshCw className="w-5 h-5" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Контент с анимацией сдвига */}
      <motion.div
        animate={{
          y: showIndicator ? pullDistance * 0.3 : 0,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default PullToRefresh;
